import { useEffect } from 'react';

/**
 * Dependency-free SEO component for the SPA.
 *
 * Renders nothing in the DOM tree but, on mount/update, sets:
 *   - <title>
 *   - <meta name="description">
 *   - Open Graph + Twitter Card meta
 *   - <link rel="canonical">
 *   - Optional JSON-LD structured data (one or many <script> blocks)
 *
 * On unmount it does NOT remove the tags — the next page's <Seo> will
 * overwrite them.  This is intentional so there's never a flash of
 * empty meta during route transitions.
 *
 * Usage:
 *   <Seo
 *     title="Speakers — ISBM 2026"
 *     description="Meet the academic leaders and industry pioneers..."
 *     path="/speakers"
 *     image="/og/speakers.jpg"            // optional, falls back to default
 *     jsonLd={[{ "@context": "https://schema.org", "@type": "Event", ... }]}
 *   />
 */

const DEFAULTS = {
  siteName:    'ISBM 2026',
  baseUrl:     import.meta.env.VITE_SITE_URL ?? 'https://isbmconf.com',
  description: 'World Conference on Information Systems for Business Management 2026, Bangkok — Springer Nature proceedings, Scopus/WoS indexed.',
  image:       '/assets/images/web/ISBM26-Logo-aw-arsh.png',
  twitter:     '@isbm_conf',
  locale:      'en_US',
};

export function Seo({
  title,
  description = DEFAULTS.description,
  path        = '',
  image       = DEFAULTS.image,
  type        = 'website',
  jsonLd      = null,
  noindex     = false,
}) {
  const fullTitle = title
    ? `${title} — ${DEFAULTS.siteName}`
    : `${DEFAULTS.siteName} — World Conference on Information Systems for Business Management`;

  const url = `${DEFAULTS.baseUrl}${path}`.replace(/\/$/, '') || DEFAULTS.baseUrl;
  const absImage = image.startsWith('http') ? image : `${DEFAULTS.baseUrl}${image}`;

  useEffect(() => {
    document.title = fullTitle;

    setMeta('name',     'description', description);
    setMeta('name',     'robots',      noindex ? 'noindex, nofollow' : 'index, follow');

    // Open Graph
    setMeta('property', 'og:type',        type);
    setMeta('property', 'og:site_name',   DEFAULTS.siteName);
    setMeta('property', 'og:title',       fullTitle);
    setMeta('property', 'og:description', description);
    setMeta('property', 'og:url',         url);
    setMeta('property', 'og:image',       absImage);
    setMeta('property', 'og:locale',      DEFAULTS.locale);

    // Twitter
    setMeta('name', 'twitter:card',        'summary_large_image');
    setMeta('name', 'twitter:title',       fullTitle);
    setMeta('name', 'twitter:description', description);
    setMeta('name', 'twitter:image',       absImage);
    setMeta('name', 'twitter:site',        DEFAULTS.twitter);

    // Canonical
    setLink('canonical', url);

    // JSON-LD — clear previous app-rendered blocks, then write new ones
    document
      .querySelectorAll('script[type="application/ld+json"][data-seo="app"]')
      .forEach((el) => el.remove());

    if (jsonLd) {
      const blocks = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      blocks.forEach((data) => {
        const s = document.createElement('script');
        s.type = 'application/ld+json';
        s.setAttribute('data-seo', 'app');
        s.textContent = JSON.stringify(data);
        document.head.appendChild(s);
      });
    }
  }, [fullTitle, description, url, absImage, type, noindex, jsonLd]);

  return null;
}

/* helpers */

function setMeta(attr, key, value) {
  if (value == null) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', String(value));
}

function setLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

/**
 * Build the conference Event JSON-LD from the (admin-editable) dates so the
 * structured data stays in sync with the rest of the site. Pass the value from
 * `useConferenceDates()`; omit to use the built-in defaults.
 */
export function buildConferenceJsonLd(dates) {
  return {
    ...CONFERENCE_JSON_LD,
    startDate: dates?.startDate ?? CONFERENCE_JSON_LD.startDate,
    endDate:   dates?.endDate ?? CONFERENCE_JSON_LD.endDate,
  };
}

/**
 * Shared JSON-LD payload for the conference (Event schema).
 * Use on homepage + CFP + Venue for richer search results.
 */
export const CONFERENCE_JSON_LD = {
  '@context':    'https://schema.org',
  '@type':       'Event',
  name:          'ISBM 2026 — World Conference on Information Systems for Business Management',
  startDate:     '2026-09-17',
  endDate:       '2026-09-19',
  eventStatus:   'https://schema.org/EventScheduled',
  eventAttendanceMode: 'https://schema.org/MixedEventAttendanceMode',
  location: {
    '@type':   'Place',
    name:      'Bangkok Marriott Hotel Sukhumvit',
    address: {
      '@type':         'PostalAddress',
      streetAddress:   '2 Sukhumvit Road Soi 57, Khlongtan-Nua, Wattana',
      addressLocality: 'Bangkok',
      postalCode:      '10110',
      addressCountry:  'TH',
    },
  },
  organizer: {
    '@type': 'Organization',
    name:    'Global Knowledge Research Foundation',
    url:     'https://gr.foundation',
  },
  url:           'https://isbm.ict4sd.org',
  description:   DEFAULTS.description,
  image:         `${DEFAULTS.baseUrl}${DEFAULTS.image}`,
};
